/**
 * The owner's own statements — step 13. Read-only, list + status + PDF
 * download, same minimal shape as every other portal table (see
 * OwnerPropertyList.tsx's doc comment): no `ownerId` filter anywhere here,
 * because `owner_statements_owner_select` already scopes the unfiltered
 * query to this owner's own rows.
 */

import { useState } from 'react';
import { useList } from '@refinedev/core';
import type { OwnerStatementDto } from 'shared';
import { api } from '../../../lib/api.js';
import { Bento, BentoCell } from '../../../components/Bento.js';
import { DataTable, type Column } from '../../../components/DataTable.js';

function payoutStatusLabel(row: OwnerStatementDto): {
  text: string;
  tone?: 'positive' | 'negative';
} {
  if (row.payoutStatus === 'PAID')
    return { text: `Paid ${row.paidAt?.slice(0, 10) ?? ''}`, tone: 'positive' };
  if (row.payoutStatus === 'PAYOUT_FAILED')
    return { text: 'Payout needs attention', tone: 'negative' };
  if (row.payoutStatus === 'TRANSFERRED') return { text: 'Transfer complete — payout pending' };
  if (row.payoutStatus === 'SETTLED_EXTERNALLY') return { text: 'Settled by check/wire' };
  return { text: 'Pending' };
}

export function OwnerStatementsPage() {
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const {
    data,
    isLoading,
    error: listError,
  } = useList<OwnerStatementDto>({
    resource: 'owner-statements',
    pagination: { mode: 'off' },
  });

  async function download(row: OwnerStatementDto) {
    setBusyId(row.id);
    setError(null);
    try {
      const { downloadUrl } = await api<{ downloadUrl: string }>(`/owner-statements/${row.id}/pdf`);
      window.open(downloadUrl, '_blank', 'noopener,noreferrer');
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not open that statement.');
    } finally {
      setBusyId(null);
    }
  }

  const columns: Column<OwnerStatementDto>[] = [
    { key: 'period', header: 'Period', render: (row) => `${row.periodStart} – ${row.periodEnd}` },
    { key: 'net', header: 'Net', numeric: true, render: (row) => `$${row.netAmount}` },
    {
      key: 'balance',
      header: 'Balance after',
      numeric: true,
      render: (row) => `$${row.balanceAfter}`,
    },
    {
      key: 'payout',
      header: 'Payout',
      render: (row) => {
        const { text, tone } = payoutStatusLabel(row);
        return (
          <span
            className={`pill${tone === 'positive' ? ' pill--positive' : tone === 'negative' ? ' pill--negative' : ''}`}
          >
            {text}
          </span>
        );
      },
    },
    {
      key: 'download',
      header: '',
      render: (row) => (
        <button
          type="button"
          className="button button--quiet"
          disabled={busyId === row.id}
          onClick={() => void download(row)}
        >
          {busyId === row.id ? 'Opening…' : 'Download PDF'}
        </button>
      ),
    },
  ];

  return (
    <section>
      <h1>Your statements</h1>
      <p className="muted">
        Frozen once generated — a later dispute affecting a payment already included here shows up
        as a line on a future statement instead.
      </p>
      {error ? <p className="alert alert--error">{error}</p> : null}
      <Bento>
        <BentoCell span="full">
          <DataTable
            columns={columns}
            rows={data?.data ?? []}
            rowKey={(row) => row.id}
            isLoading={isLoading}
            error={listError}
            emptyMessage="No statements yet."
          />
        </BentoCell>
      </Bento>
    </section>
  );
}
